import { BaseApiService, API_CONFIG, createPaginatedResponse } from './base.service';
import {
  PricingItem,
  PricingHeader,
  PricingHeaderCreate,
  Plan,
  PlanCreate,
  PlanUpdate,
  PriceListGroup,
  PriceListGroupUpsert,
  PriceList,
  PriceListCreate,
  PlanFeature,
  PaginatedResponse
} from '../../types';
import { validatePaginationParams } from '../../utils/pagination';

/**
 * Service for managing pricing-related operations including:
 * - Pricing Headers
 * - Plans and Plan Features
 * - Price Lists
 * - Pricing Items (legacy)
 */
export class PricingService extends BaseApiService {
  // Pricing Headers
  async getPricingHeaders(skip: number = 0, limit: number = 10, includeInactive: boolean = false): Promise<PricingHeader[]> {
    const { limit: validLimit } = validatePaginationParams(1, limit);
    const finalSkip = Math.max(0, Math.floor(skip));
    
    const params = new URLSearchParams({
      skip: finalSkip.toString(),
      limit: validLimit.toString()
    });
    
    return this.get(`${API_CONFIG.ENDPOINTS.PRICING_HEADERS}/?${params.toString()}`);
  }

  async getPricingHeader(id: number): Promise<PricingHeader> {
    return this.get(`${API_CONFIG.ENDPOINTS.PRICING_HEADERS}/${id}`);
  }

  async createPricingHeader(header: PricingHeaderCreate): Promise<PricingHeader> {
    return this.post(`${API_CONFIG.ENDPOINTS.PRICING_HEADERS}/`, header);
  }

  async updatePricingHeader(id: number, header: Partial<PricingHeaderCreate>): Promise<PricingHeader> {
    return this.put(`${API_CONFIG.ENDPOINTS.PRICING_HEADERS}/${id}`, header);
  }

  async deletePricingHeader(id: number): Promise<void> {
    await this.delete(`${API_CONFIG.ENDPOINTS.PRICING_HEADERS}/${id}`);
  }

  // Plans
  async getPlans(page: number = 1, pageSize: number = 10): Promise<PaginatedResponse<Plan>> {
    const { page: validPage, pageSize: validPageSize } = validatePaginationParams(page, pageSize);
    
    if (page !== validPage || pageSize !== validPageSize) {
      console.warn('Invalid pagination values detected in getPlans:', { 
        original: { page, pageSize },
        adjusted: { page: validPage, pageSize: validPageSize },
        stack: new Error().stack 
      });
    }
    
    return this.getPaginated(API_CONFIG.ENDPOINTS.PLANS, validPage, validPageSize);
  }

  async getAllPlans(limit: number = 100): Promise<Plan[]> {
    const validLimit = Math.max(1, Math.floor(limit) || 100);
    return this.get(`${API_CONFIG.ENDPOINTS.PLANS}/?skip=0&limit=${validLimit}`);
  }

  async getPlan(id: number): Promise<Plan> {
    return this.get(`${API_CONFIG.ENDPOINTS.PLANS}/${id}`);
  }

  async createPlan(plan: PlanCreate): Promise<Plan> {
    return this.post(`${API_CONFIG.ENDPOINTS.PLANS}/`, plan);
  }

  async updatePlan(id: number, plan: PlanUpdate): Promise<Plan> {
    return this.put(`${API_CONFIG.ENDPOINTS.PLANS}/${id}`, plan);
  }

  async deletePlan(id: number): Promise<void> {
    await this.delete(`${API_CONFIG.ENDPOINTS.PLANS}/${id}`);
  }

  async getPlanFeatures(planId: number): Promise<PlanFeature[]> {
    return this.get(`${API_CONFIG.ENDPOINTS.PLANS}/${planId}/features`);
  }

  // Price Lists
  async getPriceListsByServer(serverId: number, limit: number = 500): Promise<PriceList[]> {
    const url = `${API_CONFIG.ENDPOINTS.PRICE_LISTS}/?skip=0&limit=${limit}&server_id=${serverId}`;
    return this.get<PriceList[]>(url);
  }

  async getPriceLists(page: number = 1, pageSize: number = 10): Promise<PaginatedResponse<PriceList>> {
    const { page: validPage, pageSize: validPageSize, skip, limit } = validatePaginationParams(page, pageSize);
    
    if (page !== validPage || pageSize !== validPageSize) {
      console.warn('Invalid pagination values detected in getPriceLists:', { 
        original: { page, pageSize },
        adjusted: { page: validPage, pageSize: validPageSize },
        stack: new Error().stack 
      });
    }
    
    // The backend returns a direct array, not a paginated response
    const url = `${API_CONFIG.ENDPOINTS.PRICE_LISTS}/?skip=${skip}&limit=${limit}`;
    const items = await this.get<PriceList[]>(url);
    return createPaginatedResponse(items, validPage, validPageSize);
  }

  async getPriceList(id: number): Promise<PriceList> {
    return this.get(`${API_CONFIG.ENDPOINTS.PRICE_LISTS}/${id}`);
  }

  async getPriceListGroups(is_active: boolean | null = true): Promise<PriceListGroup[]> {
    const params = new URLSearchParams();
    if (is_active !== null) {
      params.append('is_active', is_active.toString());
    }
    const queryString = params.toString();
    return this.get(`${API_CONFIG.ENDPOINTS.PRICE_LISTS}/groups${queryString ? '?' + queryString : ''}`);
  }

  async createPriceListGroup(priceListGroup: PriceListGroupUpsert): Promise<PriceListGroup> {
    return this.post(`${API_CONFIG.ENDPOINTS.PRICE_LISTS}/groups`, priceListGroup);
  }

  async updatePriceListGroup(id: number, priceListGroup: PriceListGroupUpsert): Promise<PriceListGroup> {
    return this.put(`${API_CONFIG.ENDPOINTS.PRICE_LISTS}/groups/${id}`, priceListGroup);
  }

  async deletePriceListGroup(id: number): Promise<void> {
    await this.delete(`${API_CONFIG.ENDPOINTS.PRICE_LISTS}/groups/${id}`);
  }

  async createPriceList(priceList: PriceListCreate): Promise<PriceList> {
    return this.post(`${API_CONFIG.ENDPOINTS.PRICE_LISTS}/`, priceList);
  }

  async updatePriceList(id: number, priceList: Partial<PriceListCreate>): Promise<PriceList> {
    return this.put(`${API_CONFIG.ENDPOINTS.PRICE_LISTS}/${id}`, priceList);
  }

  async deletePriceList(id: number): Promise<void> {
    await this.delete(`${API_CONFIG.ENDPOINTS.PRICE_LISTS}/${id}`);
  }

  // Pricing items (legacy)
  async getPricingItems(page: number = 1, pageSize: number = 10): Promise<PaginatedResponse<PricingItem>> {
    return this.getPaginated(API_CONFIG.ENDPOINTS.PRICING_ITEMS, page, pageSize);
  }

  async getPricingItem(id: string): Promise<PricingItem> {
    return this.get(`${API_CONFIG.ENDPOINTS.PRICING_ITEMS}/${id}`);
  }

  async createPricingItem(item: Omit<PricingItem, 'id' | 'createdAt' | 'updatedAt'>): Promise<PricingItem> {
    return this.post(`${API_CONFIG.ENDPOINTS.PRICING_ITEMS}/`, item);
  }

  async updatePricingItem(id: string, item: Partial<PricingItem>): Promise<PricingItem> {
    return this.put(`${API_CONFIG.ENDPOINTS.PRICING_ITEMS}/${id}`, item);
  }

  async deletePricingItem(id: string): Promise<void> {
    await this.delete(`${API_CONFIG.ENDPOINTS.PRICING_ITEMS}/${id}`);
  }
}

export const pricingService = new PricingService();
